import { CSSProperties, FC } from "react";
import { EWidgetChartTypes } from "../types/overview.types";
import {
  CustomWidget,
  DASHBOARD_WIDGET_TYPE,
} from "../../../api/types/Overview";
import { useSelector } from "react-redux";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import { useDispatch } from "react-redux";
import SwitchChartButton from "./switchChartButton";
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { verticalLabelFormatter } from "../utils/chart";
import ChartsCustomXAxisTick from "./chartsCustomXAxisTick";
import ChartWrapper from "./chartWrapper";
import ChartsCustomYAxisTick from "./chartsCustomYAxisTick";
import { Formatter } from "recharts/types/component/DefaultLegendContent";

const defaultChartColor = "#005B90";
const defaultChartColorLeftAxis = "#3D78B1";
const defaultChartColorRightAxis = "#64D469";
const averageRefLineColor = "#229F45";
const averageRefLineStyles = {
  fontSize: 10,
  fontWeight: "bold",
  fill: averageRefLineColor,
};

export const widgetBarColorLightBlue = "#C2DCF2";

const chartColorsPN = {
  positive: {
    light: "#92CEA3",
    dark: "#229F45",
  },
  negative: {
    light: "#FF5959",
    dark: "#921A1A",
  },
};

type TWidgetChartAreaGeneratorProps = {
  averageReferenceLineValue?: number;
  barChartMargin?: { top: number; right: number; left: number; bottom: number };
  barColor?: string;
  cellItemsForSectionColor?: any[];
  chartColors?: string[];
  chartGroupingLabels?: string[];
  chartHighlightLabel?: string;
  chartHighlightValue?: string;
  chartType: EWidgetChartTypes;
  chartWrapperHeightClass?: string;
  computedChartData: any;
  customWidget?: CustomWidget;
  graphXAxisDataKey?: string;
  graphYAxisDataKey?: string;
  graphRightYAxisDataKey?: string;
  hideYAxis?: "left" | "right" | null;
  legendFormatter?: Formatter;
  legendHeading?: string;
  legendLabelFormatter?: (value: any) => string;
  legendValueFormatter?: (value: any) => string;
  legendVerticalAlignClass?: "justify-center" | "justify-start" | "justify-end";
  showAverageReferenceLineWithLabel?: string;
  showChartTypeSwitchButton?: boolean;
  showYZeroReferenceLine?: boolean;
  skipYaxisDayaKeyInLineChart?: boolean;
  specialLegendItems?: any[];
  t: any;
  tooltipEnabled?: boolean; // this setting is ignored for `graphRightYAxisDataKey` and Tooltip is shown by default
  tooltipFormatter?: any;
  useLegendValueFormatter?: boolean;
  usePNBarColors?: boolean; // Positive-Negative Bar Colors
  widgetType: DASHBOARD_WIDGET_TYPE;
  xAxisAngle?: number;
  xAxisTickLabelWidth?: number;
  xAxisTickUsesTranslation?: boolean;
  xAxisTickFormatter?: (value: any) => string;
  xAxisStyle?: CSSProperties;
  yAxisStyle?: CSSProperties;
  yAxisTickFormatter?: (value: any) => string;
};

const WidgetChartAreaGenerator: FC<TWidgetChartAreaGeneratorProps> = ({
  averageReferenceLineValue,
  barChartMargin = { top: 0, right: 0, left: -35, bottom: 0 },
  barColor = defaultChartColor,
  cellItemsForSectionColor = [],
  chartColors = [
    "#51A3A3",
    "#75485E",
    "#CB904D",
    "#DFCC74",
    "#92CEA3",
    "#FFD059",
    "#FF5959",
    "#C3E991",
    "#FF9B71",
    "#C6E2E9",
  ],
  chartGroupingLabels = [],
  chartHighlightLabel,
  chartHighlightValue,
  chartType,
  chartWrapperHeightClass = "pt-[45%]",
  computedChartData,
  customWidget,
  graphXAxisDataKey = "title",
  graphYAxisDataKey = "value",
  graphRightYAxisDataKey = "",
  hideYAxis = null,
  legendFormatter,
  legendHeading,
  legendLabelFormatter = (value: any) => value,
  legendValueFormatter = (value: any) => value,
  legendVerticalAlignClass = "justify-center",
  showAverageReferenceLineWithLabel,
  showChartTypeSwitchButton = true,
  showYZeroReferenceLine = false,
  skipYaxisDayaKeyInLineChart = false,
  specialLegendItems = [],
  t,
  tooltipEnabled = false, // this setting is ignored for `graphRightYAxisDataKey` and Tooltip is shown by default
  tooltipFormatter,
  useLegendValueFormatter = false,
  usePNBarColors = false,
  widgetType,
  xAxisAngle,
  xAxisTickLabelWidth,
  xAxisTickUsesTranslation = true,
  xAxisTickFormatter = (value: any) => value,
  xAxisStyle = { fontSize: "8px" },
  yAxisStyle = { fontSize: "8px" },
  yAxisTickFormatter = (value: any) => value,
}) => {
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const dispatch = useDispatch();

  const pieSize = 80;

  const applicableLegendItems =
    specialLegendItems.length > 0 ? specialLegendItems : computedChartData;

  const isZeroState = !!computedChartData?.length &&
  computedChartData.every((item: any) => Number(item[graphYAxisDataKey] || 0) === 0);

  switch (chartType) {
    case EWidgetChartTypes.PIE:
      return (
        <div className="w-full relative flex">
          {/* <pre className="text-[8px]">{JSON.stringify(computedChartData, null, 2)}</pre> */}
          <>
            {/* Legend */}
            <div className="w-1/2 flex items-center relative">
              <div
                className={`absolute w-full h-full flex flex-col items-start ${legendVerticalAlignClass} gap-2 pr-2 overflow-auto`}
              >
                {!!legendHeading && (
                  <h4 className="font-semibold text-sm mb-1">
                    {legendHeading}
                  </h4>
                )}
                {!!applicableLegendItems
                  ? applicableLegendItems
                      // filter out zero values for widgetType === DASHBOARD_WIDGET_TYPE.SAFETY_EVENTS
                      .filter((item: any) =>
                        widgetType === DASHBOARD_WIDGET_TYPE.SAFETY_EVENTS
                          ? item[graphYAxisDataKey] !== 0
                          : true
                      )
                      // sort in descending order by graphYAxisDataKey
                      .sort((a: any, b: any) => {
                        return (
                          (b[graphYAxisDataKey] || 0) -
                          (a[graphYAxisDataKey] || 0)
                        );
                      })
                      .map((entry: any, index: number) => (
                        <div
                          key={index}
                          className="w-full flex items-center justify-between gap-2 mb-1 text-xs"
                        >
                          <div className="flex items-center">
                            <div
                              className="shrink-0 size-3 rounded-sm mr-2"
                              style={{
                                backgroundColor:
                                  chartColors[index % chartColors.length],
                              }}
                            ></div>
                            <span className="flex-grow break-words">
                              {legendLabelFormatter(entry[graphXAxisDataKey])}
                            </span>
                          </div>
                          <span
                            className="ml-2 font-bold text-heading-gray666"
                            title={entry[graphYAxisDataKey]}
                          >
                            {useLegendValueFormatter
                              ? entry[graphYAxisDataKey] === 0
                                ? "0"
                                : verticalLabelFormatter(
                                    entry[graphYAxisDataKey]
                                  )
                              : !!legendValueFormatter
                                ? legendValueFormatter(entry[graphYAxisDataKey])
                                : entry[graphYAxisDataKey]}
                          </span>
                        </div>
                      ))
                  : []}
              </div>
            </div>

            {/* Pie Chart */}
            <div className="shrink-0 w-1/2 pt-[40%] pb-20 relative">
              {showChartTypeSwitchButton && (
                <SwitchChartButton
                  onClick={() => {
                    console.log("clicked");
                    dispatch(
                      setModalsData({
                        ...modalsState,
                        switchChartTypeForWidget: widgetType,
                        switchChartTypeForCustomWidgetId:
                          customWidget?.id || null,
                        showSwitchChartType: true,
                      })
                    );
                  }}
                />
              )}
              <ResponsiveContainer
                className={"absolute top-0 left-0 w-full h-full"}
              >
                <PieChart
                  width={pieSize}
                  height={pieSize}
                  onMouseEnter={undefined}
                >
                  {
                    isZeroState ? (
                        <Pie
                        data={[{ value: 1 }]}
                        cx="50%"
                        cy="45%"
                        outerRadius="95%"
                        fill="#D1D5DB"
                        dataKey="value"
                        isAnimationActive={false}
                      />
                    ):(
                    <Pie
                    data={!!computedChartData ? computedChartData : []}
                    cx={"50%"}
                    cy={"45%"}
                    // innerRadius={"75%"} // {pieSize - 35}
                    outerRadius={"95%"} // {pieSize - 20}
                    fill={defaultChartColor}
                    // paddingAngle={5}
                    dataKey={graphYAxisDataKey}
                  >
                    {cellItemsForSectionColor.length > 0
                      ? cellItemsForSectionColor.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={chartColors[index % chartColors.length]}
                          />
                        ))
                      : null}
                  </Pie>
                    )
                  }
                </PieChart>
              </ResponsiveContainer>
              {chartHighlightLabel && chartHighlightValue && (
                <p className="absolute bottom-0 w-full flex flex-col text-xs leading-4 text-center">
                  <span>{chartHighlightLabel}</span>
                  <span className="font-semibold">{chartHighlightValue}</span>
                </p>
              )}
            </div>
          </>
        </div>
      );
    case EWidgetChartTypes.DOUGHNUT:
      return (
        <div className="w-full relative flex">
          <>
            {/* Legend */}
            <div className="w-1/2 flex-1 flex items-center relative">
              <div
                className={`absolute w-full h-full flex flex-col items-start ${legendVerticalAlignClass} gap-2 pr-2 overflow-auto`}
              >
                {!!legendHeading && (
                  <h4 className="font-semibold text-sm mb-1">
                    {legendHeading}
                  </h4>
                )}
                {!!applicableLegendItems
                  ? applicableLegendItems
                      // filter out zero values for widgetType === DASHBOARD_WIDGET_TYPE.SAFETY_EVENTS
                      .filter((item: any) =>
                        widgetType === DASHBOARD_WIDGET_TYPE.SAFETY_EVENTS
                          ? item[graphYAxisDataKey] !== 0
                          : true
                      )
                      .map((entry: any, index: number) => (
                        <div
                          key={index}
                          className="w-full flex items-center justify-between gap-2 mb-1 text-xs"
                        >
                          <div className="flex items-center">
                            <div
                              className="shrink-0 size-3 rounded-sm mr-2"
                              style={{
                                backgroundColor:
                                  chartColors[index % chartColors.length],
                              }}
                            ></div>
                            <span className="flex-grow break-all">
                              {legendLabelFormatter(entry[graphXAxisDataKey])}
                            </span>
                          </div>
                          <span
                            className="ml-2 font-bold text-heading-gray666"
                            title={entry[graphYAxisDataKey]}
                          >
                            {useLegendValueFormatter
                              ? entry[graphYAxisDataKey] === 0
                                ? "0"
                                : verticalLabelFormatter(
                                    entry[graphYAxisDataKey]
                                  )
                              : !!legendValueFormatter
                                ? legendValueFormatter(entry[graphYAxisDataKey])
                                : entry[graphYAxisDataKey]}
                          </span>
                        </div>
                      ))
                  : []}
              </div>
            </div>

            {/* Doughnut */}
            <div className="w-1/2 flex-1 pt-[50%] relative">
              {showChartTypeSwitchButton && (
                <SwitchChartButton
                  onClick={() => {
                    dispatch(
                      setModalsData({
                        ...modalsState,
                        switchChartTypeForWidget: widgetType,
                        switchChartTypeForCustomWidgetId:
                          customWidget?.id || null,
                        showSwitchChartType: true,
                      })
                    );
                  }}
                />
              )}
              <ResponsiveContainer
                className={"absolute top-0 left-0 w-full h-full"}
              >
                <PieChart
                  width={pieSize}
                  height={pieSize}
                  onMouseEnter={undefined}
                >
                 {isZeroState ? (
                  <Pie
                    data={[{ value: 1 }]}
                    cx="50%"
                    cy="50%"
                    innerRadius="75%"
                    outerRadius="95%"
                    fill="#D1D5DB"
                    dataKey="value"
                    isAnimationActive={false}
                  />
                ) : (
                  <Pie
                    data={computedChartData || []}
                    cx="50%"
                    cy="50%"
                    innerRadius="75%"
                    outerRadius="95%"
                    fill={defaultChartColor}
                    // paddingAngle={5}
                    dataKey={graphYAxisDataKey}
                  >
                    {cellItemsForSectionColor.length > 0
                      ? cellItemsForSectionColor.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={chartColors[index % chartColors.length]}
                          />
                        ))
                      : null}
                  </Pie>
                )}
                  {chartHighlightLabel && chartHighlightValue && (
                    <foreignObject width={"100%"} height={"100%"}>
                      <div className="flex flex-col items-center justify-center w-full h-full">
                        <p className="text-center leading-[0.8vw] text-[0.7vw] text-heading-gray666 px-[20%] pt-[0.5vw]">
                          {chartHighlightLabel}
                        </p>
                        <p className="text-[1.5vw] leading-[1.5vw] font-bold text-center text-heading-gray222 mt-[0.6vw]">
                          {chartHighlightValue}
                        </p>
                      </div>
                    </foreignObject>
                  )}
                </PieChart>
              </ResponsiveContainer>
            </div>
          </>
        </div>
      );

    case EWidgetChartTypes.VERTICAL_BAR_GRAPH:
    case EWidgetChartTypes.STACKED_BAR_GRAPH:
      return (
        <ChartWrapper heightClass={chartWrapperHeightClass}>
          <>
            {showChartTypeSwitchButton && (
              <SwitchChartButton
                onClick={() => {
                  dispatch(
                    setModalsData({
                      ...modalsState,
                      switchChartTypeForWidget: widgetType,
                      switchChartTypeForCustomWidgetId:
                        customWidget?.id || null,
                      showSwitchChartType: true,
                    })
                  );
                }}
              />
            )}
            <ResponsiveContainer
              className={"absolute top-0 left-0 w-full h-full"}
            >
              <BarChart
                width={500}
                height={500 * 0.45}
                data={computedChartData}
                margin={barChartMargin}
                // barCategoryGap={10}
              >
                {usePNBarColors && (
                  <defs>
                    <linearGradient
                      id="colorPositive"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="0%"
                        stopColor={chartColorsPN.positive.light}
                        stopOpacity={1}
                      />
                      <stop
                        offset="100%"
                        stopColor={chartColorsPN.positive.dark}
                        stopOpacity={1}
                      />
                    </linearGradient>
                    <linearGradient
                      id="colorNegative"
                      x1="0"
                      y1="1"
                      x2="0"
                      y2="0"
                    >
                      <stop
                        offset="0%"
                        stopColor={chartColorsPN.negative.light}
                        stopOpacity={1}
                      />
                      <stop
                        offset="100%"
                        stopColor={chartColorsPN.negative.dark}
                        stopOpacity={1}
                      />
                    </linearGradient>
                  </defs>
                )}
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  type="category"
                  dataKey={graphXAxisDataKey}
                  tick={(props) => (
                    <ChartsCustomXAxisTick
                      {...(!!xAxisTickLabelWidth && {
                        labelWidth: xAxisTickLabelWidth,
                      })}
                      {...(!!xAxisTickUsesTranslation && { t })}
                      {...(!!xAxisTickFormatter && { t: xAxisTickFormatter })}
                      {...props}
                    />
                  )}
                  angle={xAxisAngle}
                  style={xAxisStyle}
                />
                {graphRightYAxisDataKey ? (
                  <>
                    <YAxis
                      type="number"
                      yAxisId="left"
                      dataKey={graphYAxisDataKey}
                      tickFormatter={yAxisTickFormatter}
                      style={yAxisStyle}
                      stroke={chartColors?.[0] || defaultChartColorLeftAxis}
                    />
                    <YAxis
                      type="number"
                      yAxisId="right"
                      orientation="right"
                      dataKey={graphRightYAxisDataKey}
                      tickFormatter={yAxisTickFormatter}
                      style={yAxisStyle}
                      stroke={chartColors?.[1] || defaultChartColorRightAxis}
                    />
                  </>
                ) : (
                  <YAxis
                    type="number"
                    dataKey={graphYAxisDataKey}
                    tickFormatter={yAxisTickFormatter}
                    style={yAxisStyle}
                  />
                )}
                {tooltipEnabled && <Tooltip />}
                {chartGroupingLabels.length > 0 ? ( // grouped bar chart with legend
                  <>
                    <Legend
                      // source: https://github.com/recharts/recharts/issues/923#issuecomment-1030903375
                      wrapperStyle={{
                        fontSize: "10px",
                        left: "50%",
                        transform: "translateX(-50%)",
                      }}
                    />
                    {chartGroupingLabels.map((chartGroupingLabel, index) => (
                      <Bar
                        dataKey={chartGroupingLabel}
                        fill={chartColors[index % chartColors.length]}
                        radius={
                          chartType === EWidgetChartTypes.STACKED_BAR_GRAPH
                            ? 0
                            : [5, 5, 0, 0]
                        }
                        key={`${chartGroupingLabel}_${index}`}
                        {...(chartType ===
                          EWidgetChartTypes.STACKED_BAR_GRAPH && {
                          stackId: "a",
                        })}
                      />
                    ))}
                  </>
                ) : (
                  // regular no-grouping bar chart
                  <>
                    {graphRightYAxisDataKey ? (
                      <>
                        <Bar
                          yAxisId="left"
                          dataKey={graphYAxisDataKey}
                          fill={chartColors?.[0] || defaultChartColorLeftAxis}
                          radius={[5, 5, 0, 0]}
                        />
                        <Bar
                          yAxisId="right"
                          dataKey={graphRightYAxisDataKey}
                          fill={chartColors?.[1] || defaultChartColorRightAxis}
                          radius={[5, 5, 0, 0]}
                        />
                        <Tooltip
                          labelStyle={{ marginBottom: "5px" }}
                          contentStyle={{
                            fontSize: "8px",
                            lineHeight: "6px",
                          }}
                          {...(!!tooltipFormatter && {
                            formatter: tooltipFormatter,
                          })}
                          cursor={{ fill: "#CCCCCC66" }} // backdrop color
                        />
                        <Legend
                          {...(!!legendFormatter && {
                            formatter: legendFormatter,
                          })}
                        />
                      </>
                    ) : (
                      <Bar
                        dataKey={graphYAxisDataKey}
                        fill={barColor}
                        radius={[5, 5, 0, 0]}
                        // barSize={25}
                      >
                        {cellItemsForSectionColor.length > 0
                          ? cellItemsForSectionColor?.map(
                              (resultItem, index) => (
                                <Cell
                                  cursor="pointer"
                                  fill={
                                    usePNBarColors
                                      ? resultItem[graphYAxisDataKey] >= 0
                                        ? "url(#colorPositive)"
                                        : "url(#colorNegative)"
                                      : chartColors[index % chartColors.length]
                                  }
                                  key={`cell-${index}`}
                                  radius={5}
                                />
                              )
                            )
                          : null}
                      </Bar>
                    )}
                  </>
                )}
                {!!showYZeroReferenceLine && (
                  <ReferenceLine y={0} stroke="#000" />
                )}
                {!!showAverageReferenceLineWithLabel && (
                  <ReferenceLine
                    y={
                      averageReferenceLineValue ||
                      (computedChartData as any[])?.reduce(
                        (acc, item, _, { length }) => {
                          return acc + item[graphYAxisDataKey] / length;
                        },
                        0
                      )
                    }
                    stroke={averageRefLineColor}
                    strokeWidth={2}
                    label={{
                      value: showAverageReferenceLineWithLabel,
                      position: "insideBottomLeft",
                      ...averageRefLineStyles,
                    }}
                  />
                )}
              </BarChart>
            </ResponsiveContainer>
          </>
        </ChartWrapper>
      );
    case EWidgetChartTypes.HORIZONTAL_BAR_GRAPH:
      return (
        <ChartWrapper heightClass={chartWrapperHeightClass}>
          <>
            {showChartTypeSwitchButton && (
              <SwitchChartButton
                onClick={() => {
                  dispatch(
                    setModalsData({
                      ...modalsState,
                      switchChartTypeForWidget: widgetType,
                      switchChartTypeForCustomWidgetId:
                        customWidget?.id || null,
                      showSwitchChartType: true,
                    })
                  );
                }}
              />
            )}
            <ResponsiveContainer
              className={"absolute top-0 left-0 w-full h-full"}
            >
              <BarChart
                layout="vertical"
                width={500}
                height={500 * 0.45}
                data={computedChartData}
                margin={barChartMargin}
              >
                {usePNBarColors && (
                  <defs>
                    <linearGradient
                      id="colorPositive"
                      x1="0"
                      y1="0"
                      x2="1"
                      y2="0"
                    >
                      <stop
                        offset="0%"
                        stopColor={chartColorsPN.positive.light}
                        stopOpacity={1}
                      />
                      <stop
                        offset="100%"
                        stopColor={chartColorsPN.positive.dark}
                        stopOpacity={1}
                      />
                    </linearGradient>
                    <linearGradient
                      id="colorNegative"
                      x1="1"
                      y1="0"
                      x2="0"
                      y2="0"
                    >
                      <stop
                        offset="0%"
                        stopColor={chartColorsPN.negative.light}
                        stopOpacity={1}
                      />
                      <stop
                        offset="100%"
                        stopColor={chartColorsPN.negative.dark}
                        stopOpacity={1}
                      />
                    </linearGradient>
                  </defs>
                )}
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis
                  type="number"
                  tickFormatter={yAxisTickFormatter}
                  style={xAxisStyle}
                />
                <YAxis
                  type="category"
                  dataKey={graphXAxisDataKey}
                  tick={(props) => (
                    <ChartsCustomYAxisTick
                      {...(!!xAxisTickLabelWidth && {
                        labelWidth: xAxisTickLabelWidth,
                      })}
                      {...(!!xAxisTickUsesTranslation && { t })}
                      {...props}
                    />
                  )}
                  // angle={xAxisAngle}
                  style={yAxisStyle}
                />
                {chartGroupingLabels.length > 0 ? ( // grouped bar chart with legend
                  <>
                    <Legend
                      // source: https://github.com/recharts/recharts/issues/923#issuecomment-1030903375
                      wrapperStyle={{
                        fontSize: "10px",
                        left: "50%",
                        transform: "translateX(-50%)",
                      }}
                    />
                    {chartGroupingLabels.map((chartGroupingLabel, index) => (
                      <Bar
                        dataKey={chartGroupingLabel}
                        fill={chartColors[index % chartColors.length]}
                        radius={[0, 5, 5, 0]}
                        key={`${chartGroupingLabel}_${index}`}
                      />
                    ))}
                  </>
                ) : (
                  // regular no-grouping bar chart
                  <Bar
                    dataKey={graphYAxisDataKey}
                    fill={barColor}
                    radius={[0, 5, 5, 0]}
                  >
                    {cellItemsForSectionColor.length > 0
                      ? cellItemsForSectionColor?.map((resultItem, index) => (
                          <Cell
                            cursor="pointer"
                            fill={
                              usePNBarColors
                                ? resultItem[graphYAxisDataKey] >= 0
                                  ? "url(#colorPositive)"
                                  : "url(#colorNegative)"
                                : chartColors[index % chartColors.length]
                            }
                            key={`cell-${index}`}
                            radius={5}
                          />
                        ))
                      : null}
                  </Bar>
                )}
                {!!showYZeroReferenceLine && (
                  <ReferenceLine x={0} stroke="#000" />
                )}
                {!!showAverageReferenceLineWithLabel && (
                  <ReferenceLine
                    x={
                      averageReferenceLineValue ||
                      (computedChartData as any[])?.reduce(
                        (acc, item, _, { length }) => {
                          return acc + item[graphYAxisDataKey] / length;
                        },
                        0
                      )
                    }
                    stroke={averageRefLineColor}
                    strokeWidth={2}
                    label={{
                      value: showAverageReferenceLineWithLabel,
                      position: "insideBottomRight",
                      ...averageRefLineStyles,
                    }}
                  />
                )}
              </BarChart>
            </ResponsiveContainer>
          </>
        </ChartWrapper>
      );
    case EWidgetChartTypes.LINE_GRAPH:
      return (
        <ChartWrapper heightClass={chartWrapperHeightClass}>
          <>
            {showChartTypeSwitchButton && (
              <SwitchChartButton
                onClick={() => {
                  dispatch(
                    setModalsData({
                      ...modalsState,
                      switchChartTypeForWidget: widgetType,
                      switchChartTypeForCustomWidgetId:
                        customWidget?.id || null,
                      showSwitchChartType: true,
                    })
                  );
                }}
              />
            )}
            <ResponsiveContainer
              className={"absolute top-0 left-0 w-full h-full"}
            >
              <ComposedChart
                width={500}
                height={500 * 0.45}
                data={computedChartData}
                margin={barChartMargin}
              >
                <defs>
                  <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="10%"
                      stopColor={defaultChartColor}
                      stopOpacity={0.4}
                    />
                    <stop
                      offset="90%"
                      stopColor={defaultChartColor}
                      stopOpacity={0.0}
                    />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  type="category"
                  dataKey={graphXAxisDataKey}
                  tick={(props) => (
                    <ChartsCustomXAxisTick
                      {...(!!xAxisTickLabelWidth && {
                        labelWidth: xAxisTickLabelWidth,
                      })}
                      {...(!!xAxisTickUsesTranslation && { t })}
                      {...(!!xAxisTickFormatter && { t: xAxisTickFormatter })}
                      {...props}
                    />
                  )}
                  angle={xAxisAngle}
                  style={xAxisStyle}
                />
                {graphRightYAxisDataKey ? (
                  <>
                    {hideYAxis !== "left" && (
                      <YAxis
                        type="number"
                        yAxisId="left"
                        dataKey={graphYAxisDataKey}
                        tickFormatter={yAxisTickFormatter}
                        style={yAxisStyle}
                        stroke={chartColors?.[0] || defaultChartColorLeftAxis}
                      />
                    )}
                    {hideYAxis !== "right" && (
                      <YAxis
                        type="number"
                        yAxisId="right"
                        orientation="right"
                        dataKey={graphRightYAxisDataKey}
                        tickFormatter={yAxisTickFormatter}
                        style={yAxisStyle}
                        stroke={chartColors?.[1] || defaultChartColorRightAxis}
                      />
                    )}
                  </>
                ) : (
                  <YAxis
                    type="number"
                    {...(skipYaxisDayaKeyInLineChart
                      ? {}
                      : { dataKey: graphYAxisDataKey })}
                    tickFormatter={yAxisTickFormatter}
                    style={yAxisStyle}
                  />
                )}
                {!!showYZeroReferenceLine && (
                  <ReferenceLine y={0} stroke="#000" />
                )}
                {chartGroupingLabels.length > 0 ? ( // grouped bar chart with legend
                  <>
                    <Legend
                      // source: https://github.com/recharts/recharts/issues/923#issuecomment-1030903375
                      wrapperStyle={{
                        fontSize: "10px",
                        left: "50%",
                        transform: "translateX(-50%)",
                      }}
                      {...(!!legendFormatter && {
                        formatter: legendFormatter,
                      })}
                    />
                    {tooltipEnabled && (
                      <Tooltip
                        labelStyle={{ marginBottom: "5px" }}
                        contentStyle={{
                          fontSize: "10px",
                          lineHeight: "6px",
                        }}
                        {...(!!tooltipFormatter && {
                          formatter: tooltipFormatter,
                        })}
                      />
                    )}
                    {chartGroupingLabels.map((chartGroupingLabel, index) => (
                      <Line
                        type="monotone"
                        dataKey={chartGroupingLabel}
                        stroke={chartColors[index % chartColors.length]}
                        key={`${chartGroupingLabel}_${index}`}
                      />
                    ))}
                  </>
                ) : (
                  // regular no-grouping bar chart
                  <>
                    {graphRightYAxisDataKey ? (
                      <>
                        {hideYAxis !== "left" && (
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey={graphYAxisDataKey}
                            stroke={
                              chartColors?.[0] || defaultChartColorLeftAxis
                            }
                            strokeWidth={2}
                            dot={true}
                          />
                        )}
                        {hideYAxis !== "right" && (
                          <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey={graphRightYAxisDataKey}
                            stroke={
                              chartColors?.[1] || defaultChartColorRightAxis
                            }
                            strokeWidth={2}
                            dot={true}
                          />
                        )}
                        <Tooltip
                          labelStyle={{ marginBottom: "5px" }}
                          contentStyle={{
                            fontSize: "8px",
                            lineHeight: "6px",
                          }}
                          {...(!!tooltipFormatter && {
                            formatter: tooltipFormatter,
                          })}
                        />
                        <Legend
                          {...(!!legendFormatter && {
                            formatter: legendFormatter,
                          })}
                        />
                      </>
                    ) : (
                      <>
                        <Line
                          type="monotone"
                          dataKey={graphYAxisDataKey}
                          stroke={defaultChartColor}
                          strokeWidth={2}
                          dot={false}
                        />
                        <Area
                          type="monotone"
                          dataKey={graphYAxisDataKey}
                          fillOpacity={1}
                          fill="url(#colorUv)"
                        />
                      </>
                    )}
                  </>
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </>
        </ChartWrapper>
      );
    default:
      return null;
  }
};
export default WidgetChartAreaGenerator;
